import { useState } from "react";

/**
 * Setup guides.
 *
 * These live in the app rather than a support page because every one of them
 * has a step that is easy to miss and produces a silent failure days later:
 * a permission not granted, a region that cannot be changed afterwards, an
 * export that never backfills. Each guide calls those out where they occur
 * rather than burying them in a note at the end.
 */

interface Step {
  title: string;
  body: string;
  /** Copyable value: a service account or other literal to paste. */
  copy?: string;
  /** Where to go to do this step. */
  links?: { label: string; url: string }[];
  /** Shown as a warning inside the step. */
  warn?: string;
}

interface Guide {
  id: string;
  title: string;
  summary: string;
  time: string;
  unlocks: string;
  steps: Step[];
  gotchas: { title: string; body: string }[];
  docs: { label: string; url: string }[];
}

const GUIDES: Guide[] = [
  {
    id: "gsc-export",
    title: "Search Console bulk export",
    summary:
      "Sends Search Console data to BigQuery every day. This is the single highest-value setup step: it removes the 1,000 row cap, the 16 month history limit, and the extra truncation that applies when you combine query with page.",
    time: "10 minutes",
    unlocks: "Everything in Explore and six of the seven analyses.",
    steps: [
      {
        title: "Pick or create a Google Cloud project",
        body: "Create a project or choose an existing one, then copy the project ID. Not the project number: they look similar and only the ID works.",
        links: [{ label: "Cloud Console", url: "https://console.cloud.google.com/" }],
      },
      {
        title: "Enable two APIs",
        body: "Enable BigQuery API and BigQuery Storage API in the project you just chose.",
        links: [
          { label: "Enable BigQuery API", url: "https://console.cloud.google.com/apis/library/bigquery.googleapis.com" },
          { label: "Enable BigQuery Storage API", url: "https://console.cloud.google.com/apis/library/bigquerystorage.googleapis.com" },
        ],
      },
      {
        title: "Grant the export service account access",
        body: "Open IAM, click Grant access, and paste this service account as the new principal. Give it two roles: BigQuery Job User and BigQuery Data Editor, then save.",
        copy: "search-console-data-export@system.gserviceaccount.com",
        links: [{ label: "IAM permissions", url: "https://console.cloud.google.com/iam-admin/iam" }],
        warn: "Miss this and the export silently never writes anything. It is the most common failure.",
      },
      {
        title: "Turn on the export in Search Console",
        body: "Open your property, go to Settings, then Bulk data export, and paste the Cloud project ID. You can change the dataset name and location here.",
        links: [{ label: "Search Console", url: "https://search.google.com/search-console" }],
        warn: "The dataset location cannot be changed later. If you will also export GA4, pick the same region for both, otherwise BigQuery cannot join them.",
      },
      {
        title: "Wait a day",
        body: "The first tables appear within a day or two. A dataset named searchconsole is created for you.",
      },
    ],
    gotchas: [
      {
        title: "It does not backfill",
        body: "The export only contains data from the day you set it up onward. History before that is gone for good, so turn it on for every client the moment you take them on, even before you need it.",
      },
      {
        title: "You need owner permission",
        body: "Restricted or full users cannot configure the export. You need owner on the Search Console property.",
      },
      {
        title: "One dataset covers one property",
        body: "Exporting several properties creates several datasets, each covering one property. The app reads the site URL out of each so you can tell them apart.",
      },
    ],
    docs: [
      { label: "Start a new bulk data export", url: "https://support.google.com/webmasters/answer/12917675" },
      { label: "Export table reference", url: "https://support.google.com/webmasters/answer/12917991" },
    ],
  },
  {
    id: "ga4-export",
    title: "GA4 BigQuery export",
    summary:
      "Sends raw GA4 events to BigQuery daily. Without it, the funnel stops at the landing page, because the GA4 Data API returns aggregates rather than sessions and query-level outcomes cannot be derived from aggregates at all.",
    time: "5 minutes",
    unlocks: "Query to outcome funnel, session source reliability, revenue by query.",
    steps: [
      {
        title: "Open the BigQuery link settings",
        body: "In GA4, go to Admin, then Product links, then BigQuery links, and click Link.",
        links: [{ label: "Google Analytics", url: "https://analytics.google.com/" }],
      },
      {
        title: "Choose your Cloud project",
        body: "Pick the same project holding your Search Console export, so both datasets sit together.",
      },
      {
        title: "Choose the data location",
        body: "Set the same region as your Search Console dataset.",
        warn: "Different regions means BigQuery cannot join the two, and the funnel stays unavailable. This cannot be fixed later without recreating an export.",
      },
      {
        title: "Select Daily",
        body: "Daily is what the analyses read. Streaming is optional and adds cost; it only gives you intraday data, which no analysis here needs.",
      },
      {
        title: "Confirm and wait a day",
        body: "The first events table appears the following day. A dataset named analytics_<propertyId> is created for you.",
      },
    ],
    gotchas: [
      {
        title: "It does not backfill either",
        body: "Every day you delay is a day of query-to-outcome data that will never exist. This is the only genuinely time-sensitive item in the whole setup.",
      },
      {
        title: "Older exports attribute differently",
        body: "Exports created before July 2024 lack the session-scoped traffic source record. The app reconstructs session source from event data in that case, which will not exactly match the GA4 interface. It tells you when this applies.",
      },
    ],
    docs: [
      { label: "GA4 BigQuery Export", url: "https://support.google.com/analytics/answer/9358801" },
      { label: "Export schema", url: "https://support.google.com/analytics/answer/7029846" },
    ],
  },
  {
    id: "gbp",
    title: "Business Profile access",
    summary:
      "Adds Maps and local Search impressions, calls, direction requests and website clicks. For a local business the profile often outperforms the website, and Search Console cannot see any of it.",
    time: "15 minutes, then days of waiting",
    unlocks: "Local demand, and the click-to-session gap between profile and site.",
    steps: [
      {
        title: "Set up an email on your own domain",
        body: "Do this first. The access application expects your email domain to match your website domain, and a mismatch is the most common rejection reason.",
        warn: "Applying for example.com from a gmail.com address will usually be denied.",
      },
      {
        title: "Enable the Business Profile APIs",
        body: "Enable all three: Business Profile Performance, My Business Account Management, and My Business Business Information. The app uses one for metrics and the other two to find your locations.",
        links: [
          { label: "Performance API", url: "https://console.cloud.google.com/apis/library/businessprofileperformance.googleapis.com" },
          { label: "Account Management API", url: "https://console.cloud.google.com/apis/library/mybusinessaccountmanagement.googleapis.com" },
          { label: "Business Information API", url: "https://console.cloud.google.com/apis/library/mybusinessbusinessinformation.googleapis.com" },
        ],
      },
      {
        title: "Check your quota",
        body: "Look at the quota for the Business Profile APIs. Zero means access has not been granted yet, which is the normal starting state.",
        links: [{ label: "Quotas", url: "https://console.cloud.google.com/iam-admin/quotas" }],
        warn: "If the quota is zero, do not request a quota increase. That is a different form and it will be rejected. Submit the Basic API Access application instead.",
      },
      {
        title: "Submit the Basic API Access application",
        body: "Follow the prerequisites page to the access form. Describe managing profiles you own or officially represent; anything resembling third-party data collection is screened out.",
        links: [{ label: "Prerequisites and access form", url: "https://developers.google.com/my-business/content/prereqs" }],
      },
      {
        title: "Wait for approval, then connect",
        body: "Approval takes days to weeks and can be denied with a reason. Once your quota is above zero, connect the location here and sync.",
      },
    ],
    gotchas: [
      {
        title: "The scope can edit your listing",
        body: "Business Profile only offers business.manage, which permits writing as well as reading. This app never writes, but the consent screen will say it can. Worth telling a client before you send them the link.",
      },
      {
        title: "Keywords are monthly and thresholded",
        body: "Search keywords come back per month, not per day, and low-volume terms are reported as a floor rather than a count. Those are stored flagged so a floor is never mistaken for a measurement.",
      },
      {
        title: "There is no export, so this data is stored",
        body: "Unlike Search Console and GA4, Business Profile has no BigQuery export. The app pulls from a rate-limited API and keeps a copy, which is the only integration here that does.",
      },
    ],
    docs: [
      { label: "Prerequisites and access", url: "https://developers.google.com/my-business/content/prereqs" },
      { label: "Quotas and limits", url: "https://developers.google.com/my-business/content/limits" },
      { label: "Performance API reference", url: "https://developers.google.com/my-business/reference/performance/rest" },
    ],
  },
];

export function Guides() {
  const [open, setOpen] = useState<Set<string>>(new Set([GUIDES[0].id]));
  const [copied, setCopied] = useState<string | null>(null);

  const toggle = (id: string) => setOpen((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  return (
    <div className="shell">
      <h1>Setup guides</h1>
      <p className="muted" style={{ maxWidth: "68ch" }}>
        Each of these has at least one step that fails silently days later if
        you miss it. Those are called out where they happen.
      </p>

      {GUIDES.map((g) => (
        <section className={`panel guide ${open.has(g.id) ? "open" : ""}`} key={g.id}>
          {/* The whole header is the control, so the target matches what a
              person would try to click. */}
          <button className="guide-head" onClick={() => toggle(g.id)}
                  aria-expanded={open.has(g.id)} aria-controls={`guide-${g.id}`}>
            <span className={`guide-caret ${open.has(g.id) ? "on" : ""}`} aria-hidden="true" />
            <span className="guide-headings">
              <span className="guide-title">{g.title}</span>
              <span className="guide-summary">{g.summary}</span>
              <span className="guide-unlocks"><strong>Unlocks:</strong> {g.unlocks}</span>
            </span>
            <span className="guide-meta num">{g.time}</span>
          </button>

          <div id={`guide-${g.id}`} hidden={!open.has(g.id)}>
            <ol className="guide-steps">
                {g.steps.map((s, i) => (
                  <li key={i}>
                    <div className="guide-step-title">{s.title}</div>
                    <p>{s.body}</p>
                    {s.links && (
                      <div className="guide-links">
                        {s.links.map((l) => (
                          <a key={l.url} href={l.url} target="_blank" rel="noreferrer">
                            {l.label} ↗
                          </a>
                        ))}
                      </div>
                    )}
                    {s.copy && (
                      <button className="guide-copy num"
                              onClick={async () => {
                                await navigator.clipboard.writeText(s.copy!);
                                setCopied(s.copy!);
                                setTimeout(() => setCopied(null), 1800);
                              }}>
                        {copied === s.copy ? "Copied" : s.copy}
                      </button>
                    )}
                    {s.warn && <p className="guide-warn">{s.warn}</p>}
                  </li>
                ))}
              </ol>

            <div className="guide-docs">
                <span>Google's own docs:</span>
                {g.docs.map((d) => (
                  <a key={d.url} href={d.url} target="_blank" rel="noreferrer">{d.label} ↗</a>
                ))}
              </div>

              <div className="guide-gotchas">
                <div className="guide-gotchas-title">Worth knowing before you start</div>
                {g.gotchas.map((x, i) => (
                  <div key={i}>
                    <strong>{x.title}.</strong> {x.body}
                  </div>
                ))}
              </div>
          </div>
        </section>
      ))}
    </div>
  );
}
